/**
 * GoogleDriveConnectionCard
 * =========================
 * Profile-page integration card. Connects the user's Google Drive for reading
 * shared documents referenced in Close.io email correspondence.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Badge, Button, Card } from '../ui';
import { useToast } from '../../contexts/ToastContext';
import {
  disconnectGoogleDrive,
  getGoogleDriveStatus,
  initiateGoogleDriveConnect,
} from '../../api/userIntegrations';

const messageForParam = (value) => {
  switch (value) {
    case 'connected':
      return { variant: 'success', text: 'Google Drive connected.' };
    case 'error':
      return { variant: 'danger', text: 'Google Drive authorization failed.' };
    case 'invalid_state':
      return { variant: 'danger', text: 'OAuth state invalid — please retry.' };
    case 'exchange_failed':
      return { variant: 'danger', text: 'Could not complete the Google connection. Retry.' };
    default:
      return null;
  }
};

const GoogleDriveConnectionCard = () => {
  const { showToast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getGoogleDriveStatus();
      setStatus(data);
    } catch (err) {
      console.error('Failed to load Google Drive status', err);
      setStatus({ connected: false, oauth_configured: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Surface post-redirect banner from `?googleDrive=connected|error|...`
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const flag = params.get('googleDrive');
    const msg = messageForParam(flag);
    if (msg) {
      showToast(msg.text, msg.variant === 'danger' ? 'error' : 'success');
      params.delete('googleDrive');
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
      refresh();
    }
  }, [location.search, location.pathname, navigate, refresh, showToast]);

  const handleConnect = async () => {
    setBusy(true);
    try {
      const { authorize_url } = await initiateGoogleDriveConnect();
      if (authorize_url) {
        window.location.href = authorize_url;
      } else {
        showToast('Could not start Google connection', 'error');
      }
    } catch (err) {
      const detail = err.response?.data?.detail?.error?.message || 'Connection failed';
      showToast(detail, 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Google Drive? Documents linked in emails will stop being read automatically.')) {
      return;
    }
    setBusy(true);
    try {
      await disconnectGoogleDrive();
      showToast('Google Drive disconnected', 'success');
      refresh();
    } catch (err) {
      showToast('Disconnect failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mb-8">
      <div className="p-6 sm:p-8">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Google Drive</h3>
            <p className="text-sm text-gray-600 mt-1">
              Let portal read Google Docs, Sheets, and Drive files that lead prospects share in their emails.
              Portal acts with your permissions; only documents you can access are read.
            </p>
          </div>
          {status?.connected ? (
            <Badge variant="success">Connected</Badge>
          ) : (
            <Badge variant="accent">Not connected</Badge>
          )}
        </div>

        {loading ? (
          <p className="text-sm text-gray-500">Loading status…</p>
        ) : !status?.oauth_configured ? (
          <Alert variant="warning">
            Google Drive OAuth is not configured on this deployment. Ask an administrator to set
            <code className="mx-1">GOOGLE_OAUTH_CLIENT_ID</code>/<code className="mx-1">SECRET</code>.
          </Alert>
        ) : status.connected ? (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500 block">Google Account</span>
                <span className="text-gray-900">{status.google_email || 'Unknown'}</span>
              </div>
              <div>
                <span className="text-gray-500 block">Connected</span>
                <span className="text-gray-900">
                  {status.connected_at ? new Date(status.connected_at).toLocaleString() : '—'}
                </span>
              </div>
              <div>
                <span className="text-gray-500 block">Last Used</span>
                <span className="text-gray-900">
                  {status.last_used_at ? new Date(status.last_used_at).toLocaleString() : 'Never'}
                </span>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={handleDisconnect} loading={busy}>
                Disconnect
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <Button onClick={handleConnect} loading={busy}>
              Connect Google Drive
            </Button>
            <span className="text-xs text-gray-500">You will be redirected to Google to authorize.</span>
          </div>
        )}
      </div>
    </Card>
  );
};

export default GoogleDriveConnectionCard;
